import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next"
import _ from "lodash";

import Checkbox from "Components/Form/Checkbox";
import { ReportChecklist } from "Models/report.checklist";
import {
  ListWrapper,
  ReportAsChecklist,
  ReportAsChecklistWrapper,
  ReportListFooter,
  ReportListFooterLink,
  ReportListName,
  ReportListTitle,
  ReportListWithCheckbox,
  ReportListWrapper
} from "./styles";
import { Tooltip } from "@mui/material";

interface ReportListProps {
  showReportListFooter?: boolean;
  showCheckboxForEachReport?: boolean;
  enableReportList: ReportChecklist[];
  reports: ReportChecklist[];
  setSelectedReports: (reports: ReportChecklist[]) => void;
}

export function ReportList({
  showReportListFooter = false,
  showCheckboxForEachReport = false,
  enableReportList = [],
  reports = [],
  setSelectedReports
}: ReportListProps) {
  const { t: translate } = useTranslation();

  const [reportsSelected, setReportsSelected] = useState<ReportChecklist[]>([])

  const isSelectAllEnabled = reportsSelected?.length < enableReportList.length;
  const isUnselectAllEnabled = reportsSelected?.length === enableReportList.length

  const reportListFooter = useMemo(() => showReportListFooter && (
    <ReportListFooter>
      <ReportListFooterLink
        isEnabled={isSelectAllEnabled}
        onClick={() => isSelectAllEnabled && setReportsSelected(enableReportList)}
      >{translate("selectAll")}</ReportListFooterLink>
      <ReportListFooterLink
        isEnabled={isUnselectAllEnabled}
        onClick={() => isUnselectAllEnabled && setReportsSelected([])}
      >{translate("unselectAll")}</ReportListFooterLink>
    </ReportListFooter>
  ), [showReportListFooter, isSelectAllEnabled, isUnselectAllEnabled])

  const renderCheckboxForEachReport = useCallback((report: ReportChecklist) => {
    if (!showCheckboxForEachReport) return null;

    return (
      <Checkbox
        disabled={report.isDisabled}
        checked={!!reportsSelected?.find((rs) => rs.uuid === report.uuid)}
        onChange={(_a, checked) => {
          if (checked === true) {
            setReportsSelected((prev) => ([...(prev || []), report]))
          } else {
            const currentList = _.cloneDeep(reportsSelected);
            const indexRemove = currentList.findIndex((item) => item.uuid === report.uuid);
            if (indexRemove > -1) {
              currentList.splice(indexRemove, 1);
              setReportsSelected(currentList)
            }
          }
        }}
      />
    )
  }, [reportsSelected, showCheckboxForEachReport]);

  useEffect(() => {
    setSelectedReports(reportsSelected)
  }, [reportsSelected])

  useEffect(() => {
    if (!showCheckboxForEachReport) {
      setReportsSelected([])
    }
  }, [showCheckboxForEachReport])

  return (
     <ListWrapper>
      <ReportListTitle>{translate("listOfReports")}</ReportListTitle>
      <ReportListWrapper>
        {reports.map((report) => (
          <ReportListWithCheckbox key={report.name}>
            {renderCheckboxForEachReport(report)}
            <ReportAsChecklistWrapper>
              <Tooltip title={report.name!} placement="top">
                <ReportListName disabled={report.isDisabled}>{report.name}</ReportListName>
              </Tooltip>
              {!report?.reportId && <ReportAsChecklist>{translate("checklist")}</ReportAsChecklist>}
            </ReportAsChecklistWrapper>
          </ReportListWithCheckbox>
        ))}
      </ReportListWrapper>
      {reportListFooter}
    </ListWrapper>
  )
}
